import React, { useEffect, useState } from 'react';
import { Checkbox, Intent, Position } from '@blueprintjs/core';
import Circle from '@components/Circle';
import Flex from '@components/Flex';
import PlatformFilterButton from '@components/PlatformFilterButton';
import PlatformListFilter from '@components/PlatformListFilter';
import { ToastUtils } from '@app/data/utils';
import { TeamResource } from 'dy-frontend-http-repository/lib/modules/Filter/resources';
import { fetchTaskKanbanFilterTeams } from '../../store/effects';

export interface TaskKanbanTeamsFilterProps {
    disabled?: boolean;
    selectedTeamIds: ID[];
    onChange: (teamIds: ID[]) => void;
}

export type Props = TaskKanbanTeamsFilterProps;

// Teams are compared by ID, so swapping one team for another one is applied as well
const getTeamIdsKey = (teamIds: ID[]) => [...teamIds].sort().join(',');

const TaskKanbanTeamsFilter: React.FC<Props> = ({ disabled = false, selectedTeamIds, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isTeamsInitialized, setIsTeamsInitialized] = useState(false);
    const [teams, setTeams] = useState<TeamResource[]>([]);
    const [checkedTeamIds, setCheckedTeamIds] = useState<ID[]>(selectedTeamIds);

    useEffect(() => {
        fetchTaskKanbanFilterTeams()
            .then((foundTeams) => setTeams(foundTeams))
            .catch((e) => {
                // Log
                console.error(e);

                ToastUtils.showToast({
                    message: 'Teams were NOT loaded, team filter is unavailable',
                    intent: Intent.DANGER,
                });
            })
            .finally(() => setIsTeamsInitialized(true));
    }, []);

    // Selection is owned by the board, so clearing or hydrating it outside of this popup is mirrored back
    useEffect(() => {
        setCheckedTeamIds(selectedTeamIds);
    }, [selectedTeamIds]);

    const getFilterTagTitle = () => {
        return checkedTeamIds.length > 0 ? 'Teams:' : 'Teams';
    };

    const getFilterTagLabel = () => {
        if (checkedTeamIds.length === 0) {
            return '';
        }

        const checkedTeamTitles = checkedTeamIds
            .map((teamId) => teams.find((team) => team.id === teamId))
            .filter((team): team is TeamResource => team !== undefined)
            .map((team) => team.title);

        if (checkedTeamTitles.length !== checkedTeamIds.length) {
            // At least one checked team is not part of the loaded inventory yet
            return `${checkedTeamIds.length} selected`;
        }

        return checkedTeamTitles.join(', ');
    };

    const handleCheckboxClick = (teamId: ID) => {
        if (checkedTeamIds.includes(teamId)) {
            // Team is already checked
            setCheckedTeamIds(checkedTeamIds.filter((checkedTeamId) => checkedTeamId !== teamId));
        } else {
            // Team is NOT checked yet
            setCheckedTeamIds([...checkedTeamIds, teamId]);
        }
    };

    const handleApplyFilter = () => {
        if (getTeamIdsKey(checkedTeamIds) !== getTeamIdsKey(selectedTeamIds)) {
            // Checked teams changed since opening the filter
            onChange(checkedTeamIds);
        }
    };

    const handleRemoveFilter = () => {
        setCheckedTeamIds([]);
        setIsOpen(false);

        if (selectedTeamIds.length > 0) {
            // Clearing teams restores the selected base scope instead of emptying the board
            onChange([]);
        }
    };

    const renderRow = (team: TeamResource) => {
        return (
            <li className="mb-1" key={team.id}>
                <Flex justify="space-between" align="center">
                    <Checkbox
                        checked={checkedTeamIds.includes(team.id)}
                        className="mr-2"
                        label={team.title}
                        onClick={() => handleCheckboxClick(team.id)}
                    />

                    <Circle size="11px" color={team.color} />
                </Flex>
            </li>
        );
    };

    return (
        <PlatformListFilter<TeamResource>
            isOpen={isOpen}
            loading={!isTeamsInitialized}
            removable={checkedTeamIds.length > 0}
            title="Teams"
            position={Position.BOTTOM_LEFT}
            minWidth={320}
            maxListHeight={400}
            list={teams}
            itemRenderer={renderRow}
            onApply={handleApplyFilter}
            onClose={() => setIsOpen(false)}
            onRemove={handleRemoveFilter}
        >
            <PlatformFilterButton
                icon="team"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                label={getFilterTagLabel()}
                title={getFilterTagTitle()}
            />
        </PlatformListFilter>
    );
};

export default TaskKanbanTeamsFilter;
